import React from 'react';
import { Clapperboard, ArrowRight, X, PersonStanding, Languages, DoorOpen, LogIn } from 'lucide-react';
import { useDanceStore } from '../store/useDanceStore';
import { useStorageStore } from '../store/useStorageStore';
import { useTranslation } from 'react-i18next';
import { trackEvent, hasAnalyticsConsent, setAnalyticsConsent, getOrCreateAnonymousId, initAnalytics } from '../lib/analytics';
import { LATEST_TOU_VERSION, LATEST_PRIVACY_VERSION } from '../lib/policyVersions';
import { Logo } from './Logo';
import { useResponsive } from '../contexts/ResponsiveContext';
import { AuthModal } from './AuthModal';

interface SplashOverlayProps {
  onClose: (showTooltip?: boolean) => void;
}

export const SplashOverlay: React.FC<SplashOverlayProps> = ({ onClose }) => {
  console.log("SplashOverlay Component is Rendering!");
  const { t, i18n } = useTranslation();
  const { isMobile, isTouch } = useResponsive();
  const isResponsiveMode = isTouch || isMobile;
  const setActiveModule = useDanceStore(state => state.setActiveModule);
  const setAnonymousConsentVersions = useStorageStore(state => state.setAnonymousConsentVersions);

  const [analyticsAgreed, setAnalyticsAgreed] = React.useState(() => hasAnalyticsConsent());
  const [codeChars, setCodeChars] = React.useState(() => {
    const params = new URLSearchParams(window.location.search);
    const ic = params.get('ic')?.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 3) || '';
    return [ic[0] || '', ic[1] || '', ic[2] || ''];
  });
  const invitationCode = codeChars.join('');
  const codeRefs = [
    React.useRef<HTMLInputElement>(null),
    React.useRef<HTMLInputElement>(null),
    React.useRef<HTMLInputElement>(null),
  ];
  const [validationError, setValidationError] = React.useState<string | null>(null);
  const [loading, setLoading] = React.useState(false);
  const [showAuth, setShowAuth] = React.useState(false);

  const hasSavedCode = typeof window !== 'undefined' && !!localStorage.getItem('choreographer_invitation_code');

  React.useEffect(() => {
    trackEvent('splash_screen_viewed');
    const firstEmpty = codeChars.findIndex(c => c === '');
    codeRefs[firstEmpty === -1 ? 2 : firstEmpty].current?.focus();
  }, []);

  const handleClose = () => {
    trackEvent('splash_screen_dismissed');
    setAnonymousConsentVersions(LATEST_TOU_VERSION, LATEST_PRIVACY_VERSION);
    onClose(false);
  };

  const handleJumpRightIn = async () => {
    if (!invitationCode.trim()) {
      setValidationError(t('splash.invitation_code_required', { defaultValue: 'Please enter a valid invitation code.' }));
      return;
    }

    setLoading(true);
    setValidationError(null);

    const codeSource = new URLSearchParams(window.location.search).get('ic') ? 'url_param' : 'manual';
    trackEvent('invitation_submitted', { code: invitationCode.trim(), source: codeSource });

    try {
      const anonymousId = getOrCreateAnonymousId();
      const response = await fetch(`${import.meta.env.VITE_API_URL || ''}/api/invitations/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code: invitationCode.trim(), anonymousId })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const errorMsg = errorData.error || t('splash.invitation_code_error_invalid', { defaultValue: 'Invalid code. Please try again' });
        setValidationError(errorMsg);
        trackEvent('invitation_claim_failed', { code: invitationCode.trim(), error: errorMsg });
        setLoading(false);
        return;
      }

      // Claim successful!
      localStorage.setItem('choreographer_invitation_code', invitationCode.trim());
      
      // Refresh analytics tracking to apply user property immediately
      initAnalytics(null);

      trackEvent('invitation_claim_success', { code: invitationCode.trim(), anonymous_id: anonymousId });
      trackEvent('splash_action_clicked', { action: 'jump_right_in' });
      trackEvent('splash_screen_dismissed');

      setAnonymousConsentVersions(LATEST_TOU_VERSION, LATEST_PRIVACY_VERSION);
      setActiveModule('PoseDesigner');
      onClose(true);
    } catch (err: any) {
      console.error('Failed to verify invitation code:', err);
      const generalError = t('splash.invitation_code_error_general', { defaultValue: 'Verification failed. Please check your connection and try again.' });
      setValidationError(generalError);
      trackEvent('invitation_claim_failed', { code: invitationCode.trim(), error: err?.message || 'Network error' });
      setLoading(false);
    }
  };

  const handleGoToShowcase = () => {
    trackEvent('splash_action_clicked', { action: 'go_to_showcase' });
    window.open('/showcase', '_blank');
  };

  const handleGoToTerms = () => {
    window.open('/termsofuse', '_blank');
  };

  const handleGoToPrivacy = () => {
    window.open('/privacy', '_blank');
  };

  return (
    <div className="splash-overlay-wrapper">
      <div className="splash-overlay-panel glass-panel">

        {/* Close Button - only if already verified */}
        {hasSavedCode && (
          <button 
            onClick={handleClose}
            className="splash-close-btn"
            onMouseEnter={(e) => {
              e.currentTarget.style.color = '#fff';
              e.currentTarget.style.background = 'rgba(255, 255, 255, 0.05)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.color = 'rgba(255, 255, 255, 0.4)';
              e.currentTarget.style.background = 'transparent';
            }}
          >
            <X size={18} />
          </button>
        )}

        {/* Title / Heading */}
        <div className="splash-header-wrapper">
          <div className="splash-logo-container">
            <span className="splash-welcome-banner">
              {t('splash.welcome_banner', { defaultValue: 'Welcome to' })}
            </span>
            <Logo fontSize="1.8rem" letterSpacing="4px" showAlpha={true} />
          </div>
          <h1 className="splash-heading">
            {t('splash.title', { defaultValue: 'Create Magnificent Movement' })}
          </h1>
          <p className="splash-subheading">
            {t('splash.goal_text', { defaultValue: 'A platform to design and share human kinematics' })}
          </p>
        </div>

        {/* Simplified Action Center */}
        <div className="splash-action-center">

          {/* Primary CTA: pill with embedded code input */}
          <div className="splash-cta-pill-wrapper">
            <div className={`splash-cta-pill${loading ? ' loading' : ''}${validationError ? ' error' : ''}`}>
              <div className="splash-cta-top-row">
                <PersonStanding size={20} className="splash-cta-icon" />
                <span className="splash-cta-label">
                  {loading ? t('splash.invitation_code_submitting', { defaultValue: 'Verifying…' }) : t('splash.action_jump_title', { defaultValue: 'Take the stage' })}
                </span>
              </div>
              <div
                className="splash-cta-bottom-row"
                onClick={(e) => { e.stopPropagation(); codeRefs[codeChars.findIndex(c => c === '') ?? 0]?.current?.focus(); }}
              >
                <div className="splash-cta-code-column" onClick={e => e.stopPropagation()}>
                <div className="splash-cta-code-slots">
                  {codeChars.map((char, i) => (
                    <input
                      key={i}
                      ref={codeRefs[i]}
                      type="text"
                      value={char}
                      onChange={(e) => {
                        const val = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '');
                        if (!val) return;
                        const next = [...codeChars];
                        next[i] = val[val.length - 1];
                        setCodeChars(next);
                        setValidationError(null);
                        if (i < 2) codeRefs[i + 1].current?.focus();
                      }}
                      onKeyDown={(e) => {
                        e.stopPropagation();
                        if (e.key === 'Backspace') {
                          e.preventDefault();
                          const next = [...codeChars];
                          if (char) {
                            next[i] = '';
                            setCodeChars(next);
                          } else if (i > 0) {
                            next[i - 1] = '';
                            setCodeChars(next);
                            codeRefs[i - 1].current?.focus();
                          }
                          setValidationError(null);
                        } else if (e.key === 'Enter') {
                          handleJumpRightIn();
                        } else if (e.key === 'ArrowLeft' && i > 0) {
                          codeRefs[i - 1].current?.focus();
                        } else if (e.key === 'ArrowRight' && i < 2) {
                          codeRefs[i + 1].current?.focus();
                        }
                      }}
                      onFocus={e => e.target.select()}
                      onPaste={(e) => {
                        e.preventDefault();
                        const pasted = e.clipboardData.getData('text').toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 3);
                        const next = ['', '', ''];
                        pasted.split('').forEach((c, idx) => { next[idx] = c; });
                        setCodeChars(next);
                        setValidationError(null);
                        codeRefs[Math.min(pasted.length, 2)].current?.focus();
                      }}
                      className="splash-cta-slot-input"
                      maxLength={1}
                      disabled={loading}
                      autoComplete="off"
                    />
                  ))}
                </div>
                <span className="splash-cta-code-label">Invitation Code</span>
                </div>
                <div className="splash-cta-submit-arrow-wrapper">
                  <button
                    className="splash-cta-submit-arrow"
                    onClick={(e) => { e.stopPropagation(); handleJumpRightIn(); }}
                    disabled={loading}
                    tabIndex={-1}
                  >
                    <DoorOpen size={24} />
                  </button>
                </div>
              </div>
            </div>
            {validationError && (
              <div className="splash-cta-error-row">
                {validationError}
              </div>
            )}
          </div>

          {/* Secondary Links Row */}
          <div style={{ display: 'flex', gap: '24px', alignItems: 'center', flexWrap: 'wrap', justifyContent: 'center', marginTop: '10px' }}>
            {/* View Showcase */}
            <button 
              onClick={handleGoToShowcase}
              className="splash-secondary-link"
              style={{
                background: 'none',
                border: 'none',
                color: 'rgba(255, 255, 255, 0.7)',
                fontSize: '0.9rem',
                fontWeight: 600,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                transition: 'color 0.2s'
              }}
              onMouseEnter={(e) => e.currentTarget.style.color = '#38bdf8'}
              onMouseLeave={(e) => e.currentTarget.style.color = 'rgba(255, 255, 255, 0.7)'}
            >
              <Clapperboard size={16} />
              {t('splash.action_showcase_title', { defaultValue: 'View Showcase' })}
            </button>

            {/* Sign In */}
            <button 
              onClick={() => setShowAuth(true)}
              className="splash-secondary-link splash-signin-btn"
              style={{
                background: 'none',
                border: 'none',
                color: 'rgba(255, 255, 255, 0.7)',
                fontSize: '0.9rem',
                fontWeight: 600,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                transition: 'color 0.2s'
              }}
              onMouseEnter={(e) => e.currentTarget.style.color = '#38bdf8'}
              onMouseLeave={(e) => e.currentTarget.style.color = 'rgba(255, 255, 255, 0.7)'}
            >
              <LogIn size={16} />
              {t('splash.sign_in_link', { defaultValue: 'Sign In' })}
            </button>
          </div>
        </div>

        {/* Auth Modal Portal */}
        {showAuth && <AuthModal onClose={() => setShowAuth(false)} />}

        {/* Consent & Settings Footer */}
        <div className="splash-footer" style={{ marginTop: '20px', display: 'flex', flexDirection: 'column', gap: '12px', width: '100%' }}>
          {/* Top Line: Implicit Consent Text */}
          <div style={{ fontSize: '0.78rem', color: 'rgba(255, 255, 255, 0.4)', lineHeight: 1.4, textAlign: 'left', width: '100%' }}>
            {t('splash.agreement_implicit_prefix', { defaultValue: 'By using this application, you agree to our ' })}
            <button 
              type="button" 
              onClick={(e) => { e.stopPropagation(); handleGoToTerms(); }} 
              className="splash-link-btn"
              style={{ textDecoration: 'underline', background: 'none', border: 'none', padding: 0, color: '#38bdf8', cursor: 'pointer', fontSize: 'inherit' }}
            >
              {t('splash.agreement_tou_link', { defaultValue: 'Terms of Use.' })}
            </button>
            {t('splash.agreement_implicit_and', { defaultValue: ' and acknowledge the ' })}
            <button 
              type="button" 
              onClick={(e) => { e.stopPropagation(); handleGoToPrivacy(); }} 
              className="splash-link-btn"
              style={{ textDecoration: 'underline', background: 'none', border: 'none', padding: 0, color: '#38bdf8', cursor: 'pointer', fontSize: 'inherit' }}
            >
              {t('splash.agreement_privacy_link', { defaultValue: 'Privacy Policy.' })}
            </button>
          </div>

          {/* Bottom Line: Interactive Controls (Checkbox + Language Selector) */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px', width: '100%' }}>
            {/* Analytics Consent Checkbox */}
            <label className="splash-checkbox-wrapper splash-checkbox-consent" style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', flex: 1, minWidth: '240px' }}>
              <input 
                type="checkbox" 
                checked={analyticsAgreed}
                onChange={(e) => {
                  const checked = e.target.checked;
                  setAnalyticsAgreed(checked);
                  setAnalyticsConsent(checked);
                }}
                className="splash-checkbox splash-checkbox-consent-input"
                style={{ cursor: 'pointer' }}
              />
              <span className="splash-checkbox-text" style={{ fontSize: '0.8rem', color: 'rgba(255, 255, 255, 0.6)' }}>
                {t('splash.analytics_consent_label', { defaultValue: 'Share anonymous usage statistics to help us improve.' })}
              </span>
            </label>

            {/* Language Selector */}
            <div className="splash-language-selector-container" style={{ alignSelf: 'center' }}>
              <Languages size={14} style={{ color: 'rgba(255, 255, 255, 0.6)' }} />
              <span className="splash-language-text">
                {i18n.language?.split('-')[0]?.toUpperCase() || 'EN'}
              </span>
              <span className="splash-language-arrow">▼</span>
              <select
                value={i18n.language?.split('-')[0] || 'en'}
                onChange={(e) => {
                  const newLang = e.target.value;
                  trackEvent('splash_language_changed', { language: newLang });
                  i18n.changeLanguage(newLang);
                }}
                className="splash-language-select-overlay"
              >
                <option value="en">{"\u00A0\u00A0\u00A0EN\u00A0\u00A0\u00A0"}</option>
                <option value="es">{"\u00A0\u00A0\u00A0ES\u00A0\u00A0\u00A0"}</option>
              </select>
            </div>
          </div>
        </div>

      </div>

    </div>
  );
};
